import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTenancy } from '@/contexts/TenancyContext';
// import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTable } from '@/components/ui/datatable';
import { assetRegisterColumnConfig } from '@/config/tableColumnConfig';
import { getAssetColumns } from './components/columns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Edit, Eye, Upload, Download, Search, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import './asset-register.css';
import { Asset } from './mockAssets';
import { getAssets, getAssetsByType, getAssetMenuData, createAsset, updateAsset } from '@/services/assetsService';
import { AssetRegister as AssetRegisterTypes } from '@/js/external-js/variableModules/namespaces/AssetRegister.namespace';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { renderDate } from '../../../../shared/renderDate';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { routePathByName } from '@/app/routes';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';

// Utility to map criticality/risk to badge variant
const getBadgeVariant = (level: string) => {
  switch (level.toLowerCase()) {
    case 'low':
      return 'low';
    case 'medium':
      return 'medium';
    case 'high':
      return 'high';
    case 'extreme':
      return 'extreme';
    default:
      return 'default';
  }
};

// Normalizes category names - converts legacy 'SaaS Software' to 'Software'
const normalizeCategory = (category?: string): string => {
  if (!category) return 'Uncategorized';
  if (category.toLowerCase() === 'saas software') return 'Software';
  return category;
};

// Helper to normalise "Yes"/"No" style flags that may come back in varying cases or as booleans
const normalizeYesNoFlag = (value: unknown, defaultValue = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'yes' || normalized === 'true' || normalized === 'enabled') return true;
    if (normalized === 'no' || normalized === 'false' || normalized === 'disabled') return false;
  }
  return defaultValue;
};

// Map backend asset entity to frontend asset model
const mapAssetEntityToAsset = (entity: AssetRegisterTypes.AssetEntity): Asset & Record<string, any> => {
  return {
    // Existing mapped fields (do not rename):
    id: entity.assetID,
    status: entity.status as AssetStatus,
    type: mapComponentTypeToAssetType(entity.componentType),
    dateAdded: entity.dateAdded ? renderDate(entity.dateAdded) : new Date().toISOString(),
    category: normalizeCategory(entity.assetCat),
    owner: entity.assetOwner || 'Unassigned',
    description: entity.assetDesc || entity.label || 'No description',
    criticality: mapCriticalityLevel(entity.assetCriticality),
    risk: mapRiskLevel(entity.assetRisk),
    dataClassification: mapClassification(entity.classification),
    subprocessor: normalizeYesNoFlag(entity.assetSubprocessor),
    publishInPrivacyPolicy: normalizeYesNoFlag(entity.assetPublishInPrivacyPolicy),
    annualSecurityReviewCompleted: normalizeYesNoFlag(entity.securityReview),
    vendorManagementNeeded: normalizeYesNoFlag(entity.vendorManagementNeeded),
    vendorWebsite: entity.vendorWebsite || '',
    dataTypesShared: entity.dataTypesShared || [],
    dataStorageLocations: entity.dataStorageLocations || [],
    portable: normalizeYesNoFlag(entity.portability),
    serialNumber: entity.serial,
    socCriticality: mapSocCriticality(entity.socCriticality),
    backupsRequired: normalizeYesNoFlag(entity.backupNeeded),

    // Only add backend fields that are NOT already mapped above:
    assetHistory: entity.assetHistory,
    assetLocation: entity.assetLocation,
    assetTeam: entity.assetTeam,
    backupHistoryMap: entity.backupHistoryMap,
    lastReviewDate: entity.lastReviewDate,
    nextReviewDate: entity.nextReviewDate,
    label: entity.label, // preserve raw value
    physicalLabel: entity.physicalLabel || '',
    securityReview: entity.securityReview,
    socCriticalityRaw: entity.socCriticality,
    componentId: entity.componentId || "AST-" + (entity.assetID || ''),
    componentType: entity.componentType || 'Asset', // Default to 'Asset' if not set
    relatedControls: entity.relatedControls || [],
    risks: (entity as any).assetRisks || (entity as any).risks || [],
    // Any extra fields should be added here as needed
  };
};


// Helper functions to map between backend and frontend values
const mapComponentTypeToAssetType = (componentType?: string): AssetType => {
  if (!componentType) return 'Other';
  if (componentType.toLowerCase().includes('hardware')) return 'Hardware';
  if (componentType.toLowerCase().includes('software') || componentType.toLowerCase().includes('saas')) return 'Software';
  if (componentType.toLowerCase().includes('service')) return 'Service';
  if (componentType.toLowerCase().includes('personnel')) return 'Personnel';
  if (componentType.toLowerCase().includes('document')) return 'Documentation';
  return 'Other';
};

const mapCriticalityLevel = (criticality?: string): 'High' | 'Medium' | 'Low' => {
  if (!criticality) return 'Low';
  if (criticality.toLowerCase().includes('high') || criticality.toLowerCase().includes('critical')) return 'High';
  if (criticality.toLowerCase().includes('medium') || criticality.toLowerCase().includes('moderate')) return 'Medium';
  return 'Low';
};

const mapRiskLevel = (risk?: string): 'High' | 'Medium' | 'Low' => {
  if (!risk) return 'Low';
  if (risk.toLowerCase().includes('high') || risk.toLowerCase().includes('critical')) return 'High';
  if (risk.toLowerCase().includes('medium') || risk.toLowerCase().includes('moderate')) return 'Medium';
  return 'Low';
};

const mapClassification = (classification?: string): DataClassification => {
  if (!classification) return 'Business Confidential';
  if (classification.toLowerCase().includes('customer')) return 'Customer Confidential';
  if (classification.toLowerCase().includes('public')) return 'Public';
  return 'Business Confidential';
};

const mapSocCriticality = (socCriticality?: string): 'N/A' | 'Not Critical' | 'Critical' => {
  if (!socCriticality) return 'N/A';
  if (socCriticality.toLowerCase().includes('critical')) return 'Critical';
  if (socCriticality.toLowerCase().includes('not')) return 'Not Critical';
  return 'N/A';
};

type AssetStatus = 'Active' | 'Decommissioned' | 'New Inactive';
type AssetType = 'Hardware' | 'Software' | 'Service' | 'Personnel' | 'Documentation' | 'Other';
type DataClassification = 'Customer Confidential' | 'Business Confidential' | 'Public';

/**
 * DED-1535: Added registerType prop to filter assets by componentType
 * - 'Asset' shows only assets (componentType === 'Asset' or undefined)
 * - 'Supplier' shows only suppliers (componentType === 'Supplier')
 * - 'all' (default) shows both assets and suppliers
 */
interface AssetRegisterProps {
  basePath?: string;
  isReadOnly?: boolean;
  registerType?: 'Asset' | 'Supplier' | 'all';
}

const AssetRegister: React.FC<AssetRegisterProps> = ({ basePath = '/assetdetail', isReadOnly = false, registerType = 'all' }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenancy, users, userIdToName } = useTenancy();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'decommissioned' | 'inactive'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');
  const [criticalityFilter, setCriticalityFilter] = useState<'all' | Asset['criticality']>('all');

  const [helpContent, setHelpContent] = useState(null);

  // State for the assets
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for the filter options
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [menuData, setMenuData] = useState<any>(null);

  // Track last refresh timestamp from navigation state
  const [lastRefresh, setLastRefresh] = useState<number>(0);

  // Constants
  const criticalityOptions = ['High', 'Medium', 'Low'];

  // Fetch assets and menu data
  const fetchData = useCallback(async () => {
    // DED-868: Clear assets immediately when tenancy changes to prevent stale data
    setAssets([]);
    setCategoryOptions([]);
    setMenuData(null);
    
    // Skip if no valid tenancy ID
    if (!activeTenancy?.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    // DED-868: Ensure window.companyID is synchronized before fetching
    // This prevents race conditions when switching tenancies
    if ((window as any).companyID !== activeTenancy.id) {
      (window as any).companyID = activeTenancy.id;
    }
    
    try {
      // Fetch assets (force refresh to bypass cache)
      const backendAssets = await getAssets(true);
      const mappedAssets = backendAssets.map(mapAssetEntityToAsset);
      // Filter out deleted assets
      let nonDeletedAssets = mappedAssets.filter((asset: any) => asset.assetIDstatus !== "deleted");
      
      // DED-1535: Filter by registerType (componentType)
      // componentType comes from the database, defaults to 'Asset' if not set
      console.log('[AssetRegister] Before filtering - Total assets:', nonDeletedAssets.length);
      if (registerType === 'Asset') {
        nonDeletedAssets = nonDeletedAssets.filter((asset: any) => {
          const ct = (asset.componentType || 'Asset').toLowerCase();
          return ct === 'asset';
        });
      } else if (registerType === 'Supplier') {
        nonDeletedAssets = nonDeletedAssets.filter((asset: any) => {
          const ct = (asset.componentType || 'Asset').toLowerCase();
          return ct === 'supplier';
        });
      }
      
      console.log('[AssetRegister] After filtering for', registerType, '- Filtered assets:', nonDeletedAssets.length);
      console.log('[AssetRegister] Sample componentTypes:', nonDeletedAssets.slice(0, 5).map((a: any) => ({ id: a.id, desc: a.description, componentType: a.componentType })));
      
      setAssets(nonDeletedAssets);

      // Extract unique categories
      const categories = Array.from(new Set(nonDeletedAssets.map(a => a.category).filter(Boolean)));
      setCategoryOptions(categories);

      // Fetch menu data for additional filters
      const menu = await getAssetMenuData();
      setMenuData(menu);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError(`Failed to load ${registerType === 'Supplier' ? 'suppliers' : 'assets'}. Please try again later.`);
      toast.error(`Failed to load ${registerType === 'Supplier' ? 'suppliers' : 'assets'}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTenancy?.id, registerType]);

  // Initial data fetch and when tenancy changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh data when navigating back with state refresh parameter
  useEffect(() => {
    const state = location.state as { refresh?: number } | null;
    if (state?.refresh && state.refresh !== lastRefresh) {
      console.log('[AssetRegister] Navigation state refresh detected, fetching immediately...');
      setLastRefresh(state.refresh);
      
      // Show loading toast
      const toastId = toast.loading(`Refreshing ${registerType === 'Supplier' ? 'suppliers' : 'assets'}...`);
      
      // Fetch immediately - no delay needed since hard navigation already gives backend time
      fetchData()
        .then(() => {
          toast.dismiss(toastId);
          toast.success(`${registerType === 'Supplier' ? 'Suppliers' : 'Assets'} updated!`);
        })
        .catch((error) => {
          console.error('[AssetRegister] Fetch failed:', error);
          toast.dismiss(toastId);
          toast.error('Failed to refresh data');
        });
    }
  }, [location.state, lastRefresh, registerType, fetchData]);

  // Filtering
  const filteredAssets = useMemo(() => {
    return assets.filter(a => {
      const matchesSearch = searchTerm ?
        (a?.description !== undefined && typeof a.description === 'string' && a.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (a?.id !== undefined && typeof a.id === 'string' && a.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (a?.owner !== undefined && typeof a.owner === 'string' && a.owner.toLowerCase().includes(searchTerm.toLowerCase()))
        : true;

      // Map status filter to backend status values
      const matchesStatus = statusFilter === 'all' ? true :
        (statusFilter === 'inactive' ?
          (a.status ? a.status.toLowerCase() : 'active') === 'new inactive' :
          (a.status ? a.status.toLowerCase() : 'active') === statusFilter.toLowerCase());
      const matchesCategory = categoryFilter === 'all' ? true : a.category === categoryFilter;
      const matchesCriticality = criticalityFilter === 'all' ? true : a.criticality === criticalityFilter;

      return matchesSearch && matchesStatus && matchesCategory && matchesCriticality;
    });
  }, [assets, searchTerm, statusFilter, categoryFilter, criticalityFilter]);

  // Sorting logic placeholder
  const [sortKey, setSortKey] = useState<keyof Asset>('category');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state - DED-1971: Reset pagination when filters change
  const [pageIndex, setPageIndex] = useState(0);
  
  // Reset pagination to page 1 when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, statusFilter, categoryFilter, criticalityFilter]);
  
  const sortedAssets = useMemo(() => {
    return [...filteredAssets].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return sortDir === 'asc' ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAssets, sortKey, sortDir]);

  const handleSort = (key: keyof Asset) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // --- JSON Import/Export Handlers ---
  // Export current assets as JSON file
  const handleExportJSON = () => {
    try {
      // Remove any undefined fields for cleanliness
      const cleanedAssets = assets.map(asset => JSON.parse(JSON.stringify(asset)));
      const jsonString = JSON.stringify(cleanedAssets, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${registerType === 'Supplier' ? 'supplier' : 'asset'}-register-export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${registerType === 'Supplier' ? 'Suppliers' : 'Assets'} exported as JSON`);
    } catch (err) {
      toast.error(`Failed to export ${registerType === 'Supplier' ? 'suppliers' : 'assets'} as JSON`);
    }
  };

  // Import assets from a JSON file
  const handleImportJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = JSON.parse(text);
        if (!Array.isArray(imported)) throw new Error('JSON must be an array of asset objects');
        // Validate required fields for each asset (description is required)
        const valid = imported.every((a: any) => (a.description || a.assetDesc));
        if (!valid) throw new Error('Some assets are missing required fields (description)');
        // --- Backend Sync: Persist imported assets ---
        // 1. Get current backend assets for ID comparison
        let backendAssets: any[] = [];
        try {
          backendAssets = await getAssets();
        } catch (e) {
          // fallback: treat as empty
        }
        const backendIdSet = new Set((backendAssets || []).map(a => a.assetID || a.id));
        // 2. Find max numeric ID for new asset creation
        let maxId = 0;
        [...backendAssets, ...imported].forEach(asset => {
          const idNum = parseInt(asset.assetID || asset.id, 10);
          if (!isNaN(idNum) && idNum > maxId) maxId = idNum;
        });
        // 3. For each imported asset, update or create in backend
        for (const importedAsset of imported) {
          let assetId = importedAsset.id;
          if (!assetId || assetId === '') {
            maxId++;
            assetId = maxId.toString();
            importedAsset.id = assetId;
          }
          // Convert frontend fields to backend fields if needed
          const backendAssetData = {
            ...importedAsset,
            assetID: assetId,
            assetDesc: importedAsset.description,
            assetCat: importedAsset.category,
            assetOwner: importedAsset.owner,
            status: importedAsset.status,
            // Add more mappings as needed
          };
          if (backendIdSet.has(assetId)) {
            await updateAsset(assetId, backendAssetData);
          } else {
            await createAsset(backendAssetData);
          }
        }
        // 4. Reload assets from backend to sync state
        try {
          const refreshed = await getAssets();
          const nonDeletedAssets = refreshed.filter((asset: any) => asset.assetIDstatus !== "deleted");
          setAssets(nonDeletedAssets.map(mapAssetEntityToAsset));
        } catch (e) {
          // fallback: do not update state
        }
        toast.success(`${registerType === 'Supplier' ? 'Suppliers' : 'Assets'} imported and synced to backend`);
      } catch (err: any) {
        toast.error(`Failed to import ${registerType === 'Supplier' ? 'suppliers' : 'assets'}: ` + (err.message || err));
      }
    };
    input.click();
  };


  /**
   * JSON Structure Example (for documentation):
   * [
   *   {
   *     "id": "101",
   *     "status": "Active",
   *     "type": "Hardware",
   *     "dateAdded": "2025-06-03",
   *     "category": "Laptop",
   *     "owner": "Alice",
   *     "description": "Dell Latitude 7420 Laptop",
   *     "criticality": "Medium",
   *     "risk": "Low",
   *     "dataClassification": "Business Confidential",
   *     "subprocessor": false,
   *     "publishInPrivacyPolicy": false,
   *     "annualSecurityReviewCompleted": false,
   *     "vendorManagementNeeded": false,
   *     "dataTypesShared": ["User Data"],
   *     "dataStorageLocations": ["Sydney HQ"],
   *     "portable": true,
   *     "serialNumber": "DL123456789",
   *     "physicalLabel": true,
   *     "socCriticality": "Critical",
   *     "backupsRequired": true
   *   },
   *   ...
   * ]
   */

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border'
      : 'border border-border bg-transparent text-foreground';

  useEffect(() => {
    getHelpContentByPage('asset.register').then(setHelpContent);
  }, []);

  return (
    <div className="page-container">
      <div className="glass-card">
        <div className="header-row flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="title flex flex-row">
              {registerType === 'Supplier' ? 'Supplier Register' : 'Asset Register'}
              <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              {registerType === 'Supplier' 
                ? 'Manage your suppliers and track shared data, security reviews, and compliance requirements.'
                : 'This register not only aids in identifying and managing risks but also supports the overall security processes of your organisation.'
              }
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 md:flex-nowrap">
            {/* Only show Import and Add Asset buttons in edit mode */}
            {!isReadOnly && (
              <>
                <Button
                  className="import-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleImportJSON}
                >
                  <Upload size={16} style={{ marginRight: 4 }} /> Import
                </Button>
                <Button
                  className="primary-btn"
                  size="sm"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  onClick={() => {
                    // DED-1535: Navigate to appropriate detail page based on register type
                    if (registerType === 'Supplier') {
                      navigate('/supplierdetail/new');
                    } else {
                      navigate('/assetdetail/new');
                    }
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> {registerType === 'Supplier' ? 'Add Supplier' : 'Add Asset'}
                </Button>
              </>
            )}
            {/* Always show Export button, even in read-only mode */}
            <Button
              className="export-btn"
              variant="outline"
              size="sm"
              onClick={handleExportJSON}
            >
              <Download size={16} style={{ marginRight: 4 }} /> Export
            </Button>
          </div>
        </div>
        <div className="filters-row flex flex-col md:flex-row md:items-center md:gap-4 md:justify-between my-5">
          {/* Search Bar */}
          <div className={`w-full md:w-auto md:max-w-xs ${isMobile ? 'mb-3' : ''}`}>
            <div className="relative">
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search size={16} />
              </div>
              <Input
                className="search-bar w-full pl-8"
                placeholder={registerType === 'Supplier' ? 'Search Suppliers' : 'Search Assets'}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className={`filter-controls ${isMobile ? 'mt-3' : 'mt-0'} flex flex-wrap gap-2 justify-center md:justify-end md:w-auto`}>
            {/* All Items Button */}
            <Button
              variant="outline"
              size="sm"
              className={getFilterButtonClass(statusFilter === 'all') + ' h-9 px-4 rounded-[var(--radius)] flex items-center gap-2'}
              onClick={() => setStatusFilter('all')}
            >
              {registerType === 'Supplier' ? 'All Suppliers' : 'All Assets'}
            </Button>

            {/* Status Dropdown */}
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className={getFilterButtonClass(statusFilter !== 'all') + ' min-w-[140px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {statusFilter !== 'all' ? `Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}` : 'Status'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="decommissioned">Decommissioned</SelectItem>
              </SelectContent>
            </Select>

            {/* Categories Dropdown */}
            <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as typeof categoryFilter)}>
              <SelectTrigger className={getFilterButtonClass(categoryFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {categoryFilter !== 'all' ? `Categories: ${categoryFilter}` : 'Categories'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {categoryOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Criticality Dropdown */}
            <Select value={criticalityFilter} onValueChange={v => setCriticalityFilter(v as typeof criticalityFilter)}>
              <SelectTrigger className={getFilterButtonClass(criticalityFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                <span>
                  {criticalityFilter !== 'all' ? `Criticality: ${criticalityFilter}` : 'Criticality'}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {criticalityOptions.map(option => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        {/* Responsive Asset List: DataTable for desktop, Cards for mobile */}
        {error && (
          <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md my-4">
            {error}
            <Button
              variant="outline"
              className="ml-4"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </div>
        )}

        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Loading {registerType === 'Supplier' ? 'suppliers' : 'assets'}...</span>
              </div>
            </div>
          )}
          <div className="asset-list">
            <div className="hidden md:block">
              <DataTable
                columns={getAssetColumns({
                  isReadOnly: isReadOnly,
                  onEdit: isReadOnly ? undefined : (row) => {
                    // Navigate to the asset detail page for editing
                    navigate(`${basePath}/${row.id}`);
                  },
                  onView: (row) => {
                    // Navigate to the asset detail page for viewing
                    // If in read-only mode, navigate to auditor asset detail view
                    if (isReadOnly) {
                      navigate(`/auditor-view/asset-detail/${row.id}`);
                    } else {
                      navigate(`${basePath}/${row.id}`);
                    }
                  },
                  userIdToName,
                  helpData: helpContent,
                })}
                data={sortedAssets}
                pageSize={10}
                pageIndex={pageIndex}
                onPageIndexChange={setPageIndex}
                columnDisplayConfig={assetRegisterColumnConfig}
                onRowClick={(asset) => {
                  // If using a custom path, navigate directly, otherwise use route by name
                  if (basePath !== '/assetdetail') {
                    navigate(`${basePath}/${asset.id}`);
                  } else {
                    navigate(routePathByName('asset.detail', { id: asset.id }));
                  }
                }}
              />
            </div>
          <div className="block md:hidden">
            {/* Mobile Cards */}
            {sortedAssets.map(asset => (
              <div
                className="mobile-card"
                key={asset.id}
                style={{
                  backgroundColor: theme.isDark ? '#18181b' : 'white',
                  color: theme.isDark ? '#f3f4f6' : '#18181b',
                  border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
                  boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div className="mobile-card-header">
                  <span className="mobile-card-id">{asset.category}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {!isReadOnly && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${basePath}/${asset.id}`);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(isReadOnly 
                          ? `/auditor-view/asset-detail/${asset.id}`
                          : `${basePath}/${asset.id}`);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mobile-card-name">{asset.description}</div>
                <div className="mobile-card-description">
                  Owner: {userIdToName[asset.owner] || asset.owner || 'Unassigned'}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <Badge variant={getBadgeVariant(asset.criticality)}>{asset.criticality}</Badge>
                  <Badge variant={getBadgeVariant(asset.risk)}>{asset.risk}</Badge>
                </div>
                {/* Optionally add more asset fields here if desired */}
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default AssetRegister;
